/**
 * RenderUpdates — update- en herstart-historie van de render-servers.
 * Toont per device de status + een "Nu updaten"-knop, en daaronder een tijdlijn
 * van alle bundle-update / herstart / enrollment / approval events (nachtelijk +
 * handmatig) met before/after bundle-versie en resultaat.
 * Data via /Api/render-servers + /Api/render-servers/events.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography, Button, Chip, CircularProgress, Tooltip } from '@mui/material';
import { Refresh as RefreshIcon, SystemUpdateAlt as UpdateIcon } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { bevestig } from '../../../utils/bevestig';
import { BACKEND_URL } from '../../../utils/apiConfig';
import { authFetch } from '../../../utils/authHelpers';
import { T, SectionHeader } from '../adminSettingsCommon';

const API = `${BACKEND_URL}/Api/render-servers`;

const EVENT_LABEL = {
  enroll: 'Aangemeld', approve: 'Goedgekeurd', revoke: 'Ingetrokken',
  nightly_update: 'Nachtelijke update', manual_update: 'Handmatige update', restart: 'Herstart',
};
const EVENT_COLOR = {
  nightly_update: { color: '#3b82f6', bg: '#dbeafe' },
  manual_update: { color: '#7c3aed', bg: '#ede9fe' },
  approve: { color: '#10b981', bg: '#ecfdf5' },
  revoke: { color: '#ef4444', bg: '#fee2e2' },
  enroll: { color: '#b45309', bg: '#fef3c7' },
  restart: { color: '#556071', bg: '#eef1f3' },
};
const fmtDate = (s) => (s ? new Date(s).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' }) : '—');

export default function RenderUpdates() {
  const [devices, setDevices] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, eRes] = await Promise.all([
        authFetch(API),
        authFetch(`${API}/events?limit=200`),
      ]);
      if (!dRes.ok || !eRes.ok) throw new Error();
      setDevices(await dRes.json());
      setEvents(await eRes.json());
    } catch {
      toast.error('Update-historie laden mislukt');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const hostFor = (deviceId) => devices.find(d => d.device_id === deviceId)?.hostname || deviceId.slice(0, 8);

  const requestUpdate = async (d) => {
    if (!(await bevestig({ titel: 'Update nu forceren?', tekst: 'De render-server herstart zodra hij niets rendert.', knop: 'Forceren' }))) return;
    setBusy(prev => ({ ...prev, [d.device_id]: true }));
    try {
      const res = await authFetch(`${API}/${d.device_id}/request-update`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' });
      if (!res.ok) throw new Error();
      toast.success(`Update aangevraagd voor ${d.hostname}`);
      setTimeout(fetchData, 1500);
    } catch {
      toast.error('Update aanvragen mislukt');
    } finally {
      setBusy(prev => ({ ...prev, [d.device_id]: null }));
    }
  };

  return (
    <Box>
      <SectionHeader
        title="Render updates"
        subtitle="Handmatige updates (gesigneerde bundle downloaden + verifiëren + herstart) per render-server, met resultaat en bundle-versie. Klik 'Nu updaten' om uit te rollen."
      />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button onClick={fetchData} startIcon={<RefreshIcon />} size="small" sx={{ textTransform: 'none' }}>Vernieuwen</Button>
      </Box>

      {/* Device strip with force-update */}
      {!loading && devices.filter(d => d.status === 'approved').length > 0 && (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 1.5, mb: 3 }}>
          {devices.filter(d => d.status === 'approved').map(d => (
            <Box key={d.device_id} sx={{ bgcolor: T.surface, borderRadius: '0.6rem', border: `1px solid ${T.surfaceHigh}`, p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: d.online ? '#10b981' : T.outlineVariant }} />
                <Typography sx={{ fontFamily: T.font, fontWeight: 800, fontSize: '0.9rem', color: T.text }}>{d.hostname}</Typography>
                {d.pending_command === 'update' && <Chip label="update gepland" size="small" sx={{ bgcolor: '#fef3c7', color: '#b45309', fontSize: '0.62rem', height: 18 }} />}
              </Box>
              <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.7rem', color: T.outline, mb: 1 }}>
                bundle <code>{d.app_git_sha || '—'}</code> · laatst {fmtDate(d.last_seen_at)}
              </Typography>
              <Tooltip title="Forceer nu: nieuwste gesigneerde bundle downloaden + verifiëren + herstart"><span>
                <Button size="small" variant="outlined" fullWidth disabled={!!busy[d.device_id] || d.pending_command === 'update'}
                  onClick={() => requestUpdate(d)}
                  startIcon={busy[d.device_id] ? <CircularProgress size={14} /> : <UpdateIcon />} sx={{ textTransform: 'none' }}>
                  Nu updaten
                </Button>
              </span></Tooltip>
            </Box>
          ))}
        </Box>
      )}

      {/* Event timeline */}
      <Box sx={{ bgcolor: T.surface, borderRadius: '0.6rem', border: `1px solid ${T.surfaceHigh}`, overflow: 'hidden' }}>
        <Box sx={{
          display: 'grid', gridTemplateColumns: '150px 130px 1fr 160px 70px', alignItems: 'center', gap: 1, px: 2, py: 1.25,
          bgcolor: T.surfaceLow, fontFamily: T.fontBody, fontSize: '0.65rem', fontWeight: 800,
          textTransform: 'uppercase', letterSpacing: '0.08em', color: T.outline,
        }}>
          <Box>Tijd</Box><Box>Type</Box><Box>Bericht</Box><Box>Versie</Box><Box>OK</Box>
        </Box>
        {loading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress size={20} sx={{ color: T.outlineVariant }} /></Box>
        ) : events.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', color: T.outline }}>Nog geen update-events.</Typography>
          </Box>
        ) : events.map(e => {
          const ec = EVENT_COLOR[e.event_type] || { color: T.outline, bg: T.surfaceLow };
          return (
            <Box key={e.id} sx={{
              display: 'grid', gridTemplateColumns: '150px 130px 1fr 160px 70px', alignItems: 'center', gap: 1, px: 2, py: 1.1,
              borderTop: `1px solid ${T.surfaceHigh}`, fontFamily: T.fontBody, fontSize: '0.76rem', color: T.text,
            }}>
              <Box sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: T.outline }}>{fmtDate(e.created_at)}</Box>
              <Box><Chip label={EVENT_LABEL[e.event_type] || e.event_type} size="small" sx={{ bgcolor: ec.bg, color: ec.color, fontSize: '0.64rem', fontWeight: 700, height: 20 }} /></Box>
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ fontWeight: 600, color: T.secondary }}>{hostFor(e.device_id)}</Box>
                <Box sx={{ fontSize: '0.72rem', color: T.outline, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.message || '—'}</Box>
              </Box>
              <Box sx={{ fontFamily: 'monospace', fontSize: '0.7rem', color: T.outline }}>
                {e.git_sha_before || '—'}{e.changed ? ` → ${e.git_sha_after || '?'}` : ''}
              </Box>
              <Box>
                {e.success === true ? <Chip label="✓" size="small" sx={{ bgcolor: '#ecfdf5', color: '#10b981', height: 20, fontWeight: 800 }} />
                  : e.success === false ? <Chip label="✗" size="small" sx={{ bgcolor: '#fee2e2', color: '#ef4444', height: 20, fontWeight: 800 }} />
                    : <Box sx={{ color: T.outlineVariant }}>—</Box>}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
